import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Shuffle, X, ScanLine, Loader2, MapPin, ArrowRight, Boxes, Plus, Minus, CheckCircle2,
} from "lucide-react";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_e93c448b-a938-4bcf-95ba-463bd62fed43/artifacts/2d6ua9tk_Logo_Wide_White%20%283%29.png";

export default function MoveStock() {
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [store, setStore] = useState("");
  const [scan, setScan] = useState("");
  const [product, setProduct] = useState(null); // {sku, product_title, bins:[], total_available}
  const [source, setSource] = useState(null); // selected source bin {location_code, available_qty}
  const [dest, setDest] = useState("");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const scanRef = useRef(null);

  useEffect(() => { api.get("/staff/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {}); }, []);
  const showFlash = (type, text) => { setFlash({ type, text }); setTimeout(() => setFlash(null), 2600); };

  const lookup = async () => {
    const q = scan.trim();
    if (!store) { toast.error("Pick a store first"); return; }
    if (!q) return;
    setBusy(true); setProduct(null); setSource(null); setDest("");
    try {
      const { data } = await api.get(`/inventory/stock`, { params: { q, account_id: store } });
      if (!data.found) { showFlash("warn", `No stock found for "${q}"`); }
      else if (!data.bins.length) { setProduct(data); showFlash("warn", `${data.sku} has no available stock to move`); }
      else setProduct(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const move = async () => {
    if (!source) { toast.error("Select a source bin"); return; }
    const to = dest.trim().toUpperCase();
    if (!to) { toast.error("Enter a destination bin"); return; }
    const n = Number(qty) || 0;
    if (n < 1 || n > source.available_qty) { toast.error(`Enter 1–${source.available_qty} units`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/stock/move`, {
        account_id: store, sku: product.sku, from_location: source.location_code, to_location: to, quantity: n,
      });
      showFlash("ok", `Moved ${data.moved} × ${data.sku}: ${data.from_location} → ${data.to_location}`);
      setSource(null); setDest(""); setQty(1);
      const { data: refreshed } = await api.get(`/inventory/stock`, { params: { q: product.sku, account_id: store } });
      setProduct(refreshed.found ? refreshed : null);
      setTimeout(() => scanRef.current?.focus(), 30);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!isStaff) return null;

  return (
    <div className="fixed inset-0 bg-bh-navy-deep text-white flex flex-col overflow-hidden" data-testid="move-stock">
      <header className="flex items-center justify-between px-6 py-4 bg-bh-navy/60 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-4">
          <img src={LOGO} alt="BoxHero" className="h-8" />
          <div className="h-6 w-px bg-white/20" />
          <span className="flex items-center gap-2 text-lg font-bold" style={{ fontFamily: "Manrope" }}><Shuffle className="w-6 h-6 text-bh-orange" /> Move Stock</span>
        </div>
        <button onClick={() => navigate("/admin")} data-testid="ms-exit" className="text-white/70 hover:text-white text-sm px-3 py-2 rounded-lg hover:bg-white/10 inline-flex items-center gap-2"><X className="w-5 h-5" /> Exit</button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-6">
          {/* Store + scan */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Store</label>
              <select value={store} onChange={(e) => { setStore(e.target.value); setProduct(null); setSource(null); }} data-testid="ms-store" className="w-full text-base px-3 py-3 rounded-xl bg-white text-slate-900 focus:outline-none">
                <option value="">Select…</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Scan / enter SKU or barcode</label>
              <div className="flex gap-2">
                <input ref={scanRef} autoFocus value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} data-testid="ms-scan-input" placeholder="Scan item…" className="flex-1 text-xl font-mono px-4 py-3 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-4 focus:ring-bh-orange/40" />
                <button onClick={lookup} disabled={busy} data-testid="ms-scan-btn" className="bg-bh-orange hover:bg-bh-orange-dark disabled:opacity-60 text-white font-bold px-5 rounded-xl inline-flex items-center gap-2">{busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <ScanLine className="w-6 h-6" />}</button>
              </div>
            </div>
          </div>

          {flash && (
            <div data-testid="ms-flash" className={`rounded-xl px-4 py-3 font-semibold flex items-center gap-2 ${flash.type === "ok" ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`}>
              {flash.type === "ok" ? <CheckCircle2 className="w-5 h-5" /> : <MapPin className="w-5 h-5" />} {flash.text}
            </div>
          )}

          {/* Product bins */}
          {product && (
            <div data-testid="ms-product" className="space-y-4">
              <div className="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-extrabold truncate text-white" style={{ fontFamily: "Manrope" }}>{product.product_title || product.sku}</h2>
                  <p className="text-xs text-white/50 font-mono">{product.sku}{product.barcode ? ` · ${product.barcode}` : ""}</p>
                </div>
                <span className="text-sm text-white/60 shrink-0"><b className="text-white">{product.total_available}</b> available</span>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5"><Boxes className="w-3.5 h-3.5" /> Current bins — tap a source</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="ms-bins">
                  {product.bins.map((b) => (
                    <button key={b.location_code} onClick={() => { setSource(b); setQty(Math.min(1, b.available_qty) || 1); }} data-testid={`ms-bin-${b.location_code}`} className={`rounded-xl px-4 py-3 text-left border transition-colors ${source?.location_code === b.location_code ? "bg-bh-orange border-transparent text-white" : "bg-white/5 border-white/15 hover:bg-white/10"}`}>
                      <p className="text-lg font-extrabold font-mono">{b.location_code}</p>
                      <p className="text-xs opacity-80">{b.available_qty} units</p>
                    </button>
                  ))}
                  {product.bins.length === 0 && <p className="text-sm text-white/40 col-span-full">No available stock in any bin.</p>}
                </div>
              </div>

              {/* Move panel */}
              {source && (
                <div className="rounded-2xl bg-white/5 border border-white/15 p-5 space-y-4" data-testid="ms-move-panel">
                  <div className="flex items-center gap-3 text-lg font-bold">
                    <span className="font-mono bg-white/10 px-3 py-1.5 rounded-lg">{source.location_code}</span>
                    <ArrowRight className="w-6 h-6 text-bh-orange" />
                    <input value={dest} onChange={(e) => setDest(e.target.value.toUpperCase())} data-testid="ms-dest-input" placeholder="Destination bin" className="flex-1 text-lg font-mono uppercase px-3 py-2 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-4 focus:ring-bh-orange/40" />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="ms-qty-minus" className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center"><Minus className="w-5 h-5" /></button>
                      <input type="number" min="1" max={source.available_qty} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(source.available_qty, Number(e.target.value) || 1)))} data-testid="ms-qty" className="w-24 text-center text-2xl font-extrabold px-2 py-2 rounded-xl bg-white text-slate-900" />
                      <button onClick={() => setQty((q) => Math.min(source.available_qty, q + 1))} data-testid="ms-qty-plus" className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center"><Plus className="w-5 h-5" /></button>
                    </div>
                    <button onClick={() => setQty(source.available_qty)} data-testid="ms-qty-all" className="text-xs text-white/60 hover:text-white underline">Move all ({source.available_qty})</button>
                  </div>
                  <button onClick={move} disabled={busy} data-testid="ms-move-btn" className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold text-lg px-6 py-4 rounded-2xl">{busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Shuffle className="w-6 h-6" />} Move {qty} unit{qty > 1 ? "s" : ""}</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
